import React, { useEffect } from "react";
import { Row, Col } from "reactstrap";
import { useFormik } from "formik";
import PropTypes from "prop-types";
import { serviceSchema, serviceDefaults } from "../../../../models/service";
import { ITEM_TYPES } from "../constants";
import { OverlayLoader, NeoCard, NeoInput, NeoButton, NeoBadge } from "../../../../components/Common";
import { formatCurrency } from "../../../../utils/format";

/**
 * ServiceEditor
 * Formulário principal de Produtos e Serviços.
 * Migrado para o Neo Design System.
 */
const ServiceEditor = ({ item, onSave, onDelete, isSaving, onChange }) => {

    const formik = useFormik({
        initialValues: serviceDefaults,
        validationSchema: serviceSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            onSave(values);
        },
    });

    // Sincroniza estado quando o item selecionado muda
    useEffect(() => {
        if (item) {
            formik.resetForm({
                values: {
                    ...serviceDefaults,
                    ...item
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item]);

    // Notifica mudança para o pai (isDirty)
    useEffect(() => {
        if (onChange) onChange(formik.dirty);
    }, [formik.dirty, onChange]);

    // Estado vazio
    if (!item) {
        return (
            <div className="h-100 d-flex align-items-center justify-content-center bg-light-subtle rounded-0 border border-dashed p-5">
                <div className="text-center">
                    <div className="avatar-lg mx-auto mb-4">
                        <div className="avatar-title bg-primary-subtle text-primary rounded-circle display-4">
                            <i className="mdi mdi-package-variant-closed"></i>
                        </div>
                    </div>
                    <h5>Selecione um item</h5>
                    <p className="text-muted">Escolha um produto ou serviço na lista ao lado ou crie um novo para gerenciar seus itens de venda.</p>
                </div>
            </div>
        );
    }

    const typeBadge = formik.values.type === "product"
        ? { color: "info", label: "Produto" }
        : { color: "primary", label: "Serviço" };

    return (
        <NeoCard
            title={
                <span className="d-flex align-items-center gap-2">
                    <span className="text-truncate">{formik.values.id ? formik.values.nameService : "Novo Item"}</span>
                    {formik.values.id && (
                        <>
                            <NeoBadge color={typeBadge.color}>
                                {typeBadge.label}
                            </NeoBadge>
                            {formik.values.inactive && (
                                <NeoBadge color="danger">
                                    Inativo
                                </NeoBadge>
                            )}
                        </>
                    )}
                </span>
            }
            subtitle={formik.values.id ? formatCurrency(formik.values.value) : "Preencha os dados do item"}
            headerIcon={formik.values.type === 'product' ? 'mdi-package-variant' : 'mdi-room-service'}
            headerActions={
                <div className="d-flex gap-2 flex-shrink-0">
                    {formik.values.id && (
                        <NeoButton
                            variant="outline-danger"
                            square={true}
                            onClick={() => onDelete(formik.values.id, formik.values.nameService)}
                            disabled={isSaving}
                            title="Excluir Item"
                            icon="mdi-trash-can-outline"
                        />
                    )}
                    <NeoButton
                        variant="primary"
                        onClick={formik.handleSubmit}
                        loading={isSaving}
                        loadingText="Salvando..."
                    >
                        Salvar
                    </NeoButton>
                </div>
            }
            className="overflow-hidden rounded-0 position-relative animate__animated animate__fadeIn"
            bodyClassName="p-4"
            noBorder={true}
        >
            <OverlayLoader show={isSaving} label="Salvando..." />

            {/* Dados Principais */}
            <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-4 d-flex align-items-center">
                <i className="mdi mdi-information-outline me-2 font-size-16 text-primary"></i>
                Dados Principais
            </h6>
            <Row className="mb-4">
                <Col md="6" className="mb-3">
                    <NeoInput
                        label="Nome do Item"
                        name="nameService"
                        type="text"
                        flat={true}
                        placeholder="Ex: Taxa de Adesão, Garrafinha, etc."
                        error={formik.touched.nameService && formik.errors.nameService ? formik.errors.nameService : ""}
                        {...formik.getFieldProps("nameService")}
                    />
                </Col>
                <Col md="3" className="mb-3">
                    <NeoInput
                        label="Tipo"
                        name="type"
                        type="select"
                        flat={true}
                        {...formik.getFieldProps("type")}
                    >
                        {ITEM_TYPES.map(t => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                    </NeoInput>
                </Col>
                <Col md="3" className="mb-3">
                    <NeoInput
                        label="Valor (R$)"
                        name="value"
                        type="number"
                        flat={true}
                        error={formik.touched.value && formik.errors.value ? formik.errors.value : ""}
                        {...formik.getFieldProps("value")}
                    />
                </Col>
            </Row>

            {/* Parcelas */}
            <Row className="mb-4">
                <Col md="3" className="mb-3">
                    <NeoInput
                        label="Parc. Máximas"
                        name="maxAmountInstallments"
                        type="number"
                        flat={true}
                        error={formik.touched.maxAmountInstallments && formik.errors.maxAmountInstallments ? formik.errors.maxAmountInstallments : ""}
                        {...formik.getFieldProps("maxAmountInstallments")}
                    />
                </Col>
            </Row>

            {/* Estoque e Custos — Só para Produtos */}
            {formik.values.type === "product" && (
                <>
                    <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-4 d-flex align-items-center">
                        <i className="mdi mdi-package-variant me-2 font-size-16 text-primary"></i>
                        Controle de Estoque e Custos
                    </h6>
                    <Row className="mb-4">
                        <Col md="4" className="mb-3">
                            <NeoInput
                                label="Valor de Custo (R$)"
                                name="costPrice"
                                type="number"
                                flat={true}
                                placeholder="Quanto a academia paga"
                                error={formik.touched.costPrice && formik.errors.costPrice ? formik.errors.costPrice : ""}
                                {...formik.getFieldProps("costPrice")}
                            />
                        </Col>
                        <Col md="4" className="mb-3">
                            <NeoInput
                                label="Estoque Atual Base"
                                name="stock"
                                type="number"
                                flat={true}
                                placeholder="Itens em estoque"
                                error={formik.touched.stock && formik.errors.stock ? formik.errors.stock : ""}
                                {...formik.getFieldProps("stock")}
                            />
                        </Col>
                        <Col md="4" className="mb-3">
                            <NeoInput
                                label="Alerta de Estoque Mínimo"
                                name="minimumStock"
                                type="number"
                                flat={true}
                                placeholder="Avisar em X itens"
                                error={formik.touched.minimumStock && formik.errors.minimumStock ? formik.errors.minimumStock : ""}
                                {...formik.getFieldProps("minimumStock")}
                            />
                        </Col>
                    </Row>
                </>
            )}

            {/* Configurações */}
            <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-4 d-flex align-items-center">
                <i className="mdi mdi-cog-outline me-2 font-size-16 text-primary"></i>
                Configurações
            </h6>
            <Row>
                <Col md="4" className="mb-3">
                    <div className="neo-input-wrapper neo-input-wrapper-md">
                        <label className="neo-input-label text-danger">Item Inativo</label>
                        <div className="form-check form-switch pt-2">
                            <input
                                type="checkbox"
                                role="switch"
                                checked={formik.values.inactive === true}
                                onChange={(e) => formik.setFieldValue("inactive", e.target.checked)}
                                className="form-check-input custom-switch-danger cursor-pointer"
                                id="switch-inactive"
                            />
                            <label className="ms-2 small fw-bold text-danger cursor-pointer" htmlFor="switch-inactive">
                                Item Inativo
                            </label>
                        </div>
                        <small className="text-muted ms-1 d-block mt-1">Ocultar de todo o sistema.</small>
                    </div>
                </Col>

                {formik.values.type === "service" && (
                    <>
                        <Col md="4" className="mb-3">
                            <div className="neo-input-wrapper neo-input-wrapper-md">
                                <label className="neo-input-label">Permite Entradas (Acesso)</label>
                                <div className="form-check form-switch pt-2">
                                    <input
                                        type="checkbox"
                                        role="switch"
                                        checked={formik.values.allowEntries === true}
                                        onChange={(e) => formik.setFieldValue("allowEntries", e.target.checked)}
                                        className="form-check-input custom-switch-primary cursor-pointer"
                                        id="switch-entries"
                                    />
                                    <label className="ms-2 small fw-bold cursor-pointer" htmlFor="switch-entries">
                                        Permite Entradas (Acesso)
                                    </label>
                                </div>
                                <small className="text-muted ms-1 d-block mt-1">Liberar catraca/leitora na compra.</small>
                            </div>
                        </Col>
                        <Col md="4" className="mb-3">
                            <div className="neo-input-wrapper neo-input-wrapper-md">
                                <label className="neo-input-label">Aula Experimental</label>
                                <div className="form-check form-switch pt-2">
                                    <input
                                        type="checkbox"
                                        role="switch"
                                        checked={formik.values.experimentalClass === true}
                                        onChange={(e) => formik.setFieldValue("experimentalClass", e.target.checked)}
                                        className="form-check-input custom-switch-primary cursor-pointer"
                                        id="switch-experimental"
                                    />
                                    <label className="ms-2 small fw-bold cursor-pointer" htmlFor="switch-experimental">
                                        Vincular a Aula Experimental
                                    </label>
                                </div>
                                <small className="text-muted ms-1 d-block mt-1">Vincular a esta opção pedagógica.</small>
                            </div>
                        </Col>
                    </>
                )}
            </Row>
        </NeoCard>
    );
};

ServiceEditor.propTypes = {
    item: PropTypes.object,
    onSave: PropTypes.func,
    onDelete: PropTypes.func,
    isSaving: PropTypes.bool,
    onChange: PropTypes.func
};

export default ServiceEditor;
